/*---------------------------------------------------------------------------*
 *  APP
 *---------------------------------------------------------------------------*
 *
 *  Main script for the application.
 *
 *  @package     Happy Car
 *  @author      PubliOne – Luca Rosaldi
 *  @author_uri  https://publione.it
 *
 */

import Toggler   from './modules/toggler/toggler.js';
import Tabs      from './modules/tabs/tabs.js';

import Odometer  from 'odometer';
import Scrollama from 'scrollama';
import Parallax  from 'parallax-js';

import Flickity             from 'flickity';
import FlickityNav          from 'flickity-as-nav-for';
import FlickityImagesLoaded from 'flickity-imagesloaded';
import FlickityFade         from 'flickity-fade';
import FlickityHash         from 'flickity-hash';


const App = () => {

  /**
   * DOM elements.
   * @type {Node}
   */
  const htmlEl = document.documentElement;
  const bodyEl = document.body;

  /**
   * Whether user prefers reduced motion.
   * @type {Bool}
   */
  const userPrefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Add a class to <html> element which indicates that the user is tabbing.
   *
   * @param  {Event} event
   * @return {void}
   */
  const addUserIsTabbingClass = ( event ) => {
    if ( event.keyCode === 9 ) {  // tab key
      htmlEl.classList.add( 'user-is-tabbing' );
      window.removeEventListener( 'keydown', addUserIsTabbingClass );
    }
  };

  /**
   * Disable submit button inside a form.
   *
   * @param  {Node} form
   * @return {void}
   */
  const disableFormSubmitButton = ( form ) => {
    const submitButton = form.querySelector( '[type="submit"]' );
    if ( submitButton ) {
      submitButton.setAttribute( 'disabled', 'disabled' );
      submitButton.setAttribute( 'aria-state', 'busy' );
    }
  };

  /**
   * Enable submit button inside a form.
   *
   * @param  {Node} form
   * @return {void}
   */
  const enableFormSubmitButton = ( form ) => {
    const submitButton = form.querySelector( '[type="submit"]' );
    if ( submitButton ) {
      submitButton.removeAttribute( 'disabled' );
      submitButton.removeAttribute( 'aria-state' );
    }
  };

  /**
   * Initialize Carousel module.
   *
   * Can be configured with data-attributes:
   *
   * [data-carousel-mode]            {String}  default 'carousel'
   * [data-carousel-loop]            {Boolean} default true
   * [data-carousel-draggable]       {Boolean} default true
   * [data-carousel-nav]             {Boolean} default true
   * [data-carousel-controls]        {Boolean} default true
   * [data-carousel-autoplay]        {Integer} default 0 (milliseconds)
   * [data-carousel-align]           {String}  default 'center'
   * [data-carousel-group-cells]     {Boolean} default false
   * [data-carousel-nav-for]         {String}  css selector
   * [data-carousel-attraction]      {Float}   default 0.025
   * [data-carousel-friction]        {Float}   default 0.28
   * [data-carousel-scroll-to]       {String}  css selector
   * [data-carousel-slide-on-click]  {Boolean} default false
   *
   * @uses Flickity.js
   *
   * @param  {Node}          elem
   * @param  {Object}        options
   * @return {Flickity}
   */
  const Carousel = ( elem, options ) => {

    // init only if at least two slides are present
    if ( elem.childElementCount < 2 ) {
      return;
    }

    // merge options defaults with args
    options = Object.assign({
      adaptiveHeight:       false,
      contain:              true,
      imagesLoaded:         true,
      pauseAutoPlayOnHover: true,
      hash:                 true,
      rightToLeft:          document.documentElement.getAttribute( 'dir' ) === 'rtl'
    }, options );

    // get data-attributes
    const mode         = elem.getAttribute( 'data-carousel-mode' );
    const loop         = elem.getAttribute( 'data-carousel-loop' );
    const fade         = elem.getAttribute( 'data-carousel-fade' );
    const draggable    = elem.getAttribute( 'data-carousel-draggable' );
    const nav          = elem.getAttribute( 'data-carousel-nav' );
    const controls     = elem.getAttribute( 'data-carousel-controls' );
    const autoplay     = elem.getAttribute( 'data-carousel-autoplay' );
    const startFrom    = elem.getAttribute( 'data-carousel-start-from' );
    const align        = elem.getAttribute( 'data-carousel-align' );
    const groupCells   = elem.getAttribute( 'data-carousel-group-cells' );
    const navFor       = elem.getAttribute( 'data-carousel-nav-for' );
    const attraction   = elem.getAttribute( 'data-carousel-attraction' );
    const friction     = elem.getAttribute( 'data-carousel-friction' );
    const scrollTo     = elem.getAttribute( 'data-carousel-scroll-to' );
    const slideOnClick = elem.getAttribute( 'data-carousel-slide-on-click' );

    if ( loop )       options.wrapAround         = ( loop === 'true' );
    if ( fade )       options.fade               = ( fade === 'true' );
    if ( draggable )  options.draggable          = ( draggable === 'true' );
    if ( nav )        options.pageDots           = ( nav === 'true' );
    if ( controls )   options.prevNextButtons    = ( controls === 'true' );
    if ( autoplay )   options.autoPlay           = parseInt( autoplay );
    if ( startFrom )  options.initialIndex       = parseInt( startFrom );
    if ( align )      options.cellAlign          = align;
    if ( groupCells ) options.groupCells         = ( groupCells === 'true' ) ? true : groupCells;
    if ( navFor )     options.asNavFor           = navFor;
    if ( attraction ) options.selectedAttraction = parseFloat( attraction );
    if ( friction )   options.friction           = parseFloat( friction );

    if ( mode === 'slider' ) {
      options.wrapAround      = options.wrapAround || false;
    }
    if ( navFor ) {
      options.wrapAround      = options.wrapAround || false;
      options.draggable       = options.draggable || false;
      options.pageDots        = options.pageDots || false;
      options.prevNextButtons = options.prevNextButtons || false;
    }
    if ( userPrefersReducedMotion ) {
      if ( mode === 'slider' ) options.fade = true;
      options.autoPlay = false;
    }

    var flickity = new Flickity( elem, options );

    // click selects slide
    if ( slideOnClick ) {
      flickity.on( 'staticClick', ( event, pointer, cellElement, cellIndex ) => {
        flickity.selectCell( cellIndex );
      });
    }

    // scroll to specific element on slide change
    if ( scrollTo ) {
      const target = document.querySelector( scrollTo );
      if ( target ) flickity.on( 'change', () => target.scrollIntoView( { behavior: 'smooth' } ) );
    }

    // prevent page jumping after clicking on navigation links
    if ( navFor ) {
      elem.addEventListener( 'click', ( event ) => {
        if ( event.currentTarget != flickity.selectedElement ) event.preventDefault();
      });
    }

    // scrollstep compatibility
    flickity.on( 'select', () => flickity.selectedElement.classList.add( 'is-in-viewport' ) );
    flickity.on( 'settle', () => {
      flickity.slides.forEach( ( slide ) => {
        slide = slide.cells[0].element;
        if ( slide !== flickity.selectedElement ) slide.classList.remove( 'is-in-viewport' );
      });
    });

    return flickity;
  };

  /**
   * Initialize Scrollstep module.
   *
   * @uses scrollama.js
   *
   * @return {Scrollama}
   */
  const Scrollstep = ( selector, options ) => {

    options = Object.assign({
      offset: 0.7,
      order:  true
    }, options );

    options.step = selector;

    const observer = new Scrollama().setup( options );

    observer.onStepEnter( ( step ) => {
      step.element.classList.add( 'is-in-viewport' );
      step.element.classList.add( 'was-in-viewport' );
    });
    observer.onStepExit( ( step ) => {
      step.element.classList.remove( 'is-in-viewport' );
    });

    return observer;
  };

  /**
   * Initialize Password Mask module.
   *
   * @uses passwordMask
   *
   * @return {PasswordMask}
   */
  const PasswordMask = ( elem, options ) => {
    return new PasswordMask( elem, options );
  };

  /**
   * Initialize Headroom module.
   *
   * @uses Headroom
   *
   * @return {Headroom}
   */
  const Headroom = ( elem ) => {
    htmlEl.classList.add( 'js-module-headroom' );

    elem.Headroom = new Headroom( elem, {
      offset: elem.offsetHeight,
      tolerance: {
        up:   10,
        down: 0
      },
      classes: {
        initial:   'm-headroom',
        pinned:    '-pinned',
        unpinned:  '-unpinned',
        top:       '-top',
        notTop:    '-not-top',
        bottom:    '-bottom',
        notBottom: '-not-bottom'
      }
    });
    elem.Headroom.init();
  };

  /**
   * Initialize Odometer module.
   *
   * @uses Odometer
   *
   * @return {Odometer}
   */
  const odometerInit = ( elem ) => {

    const from   = elem.getAttribute( 'data-odometer-from' )        || 0;
    const to     = elem.getAttribute( 'data-odometer-to' )          || elem.innerHTML;
    const dur    = elem.getAttribute( 'data-odometer-duration' )    || 3000;
    const format = elem.getAttribute( 'data-odometer-format' )      || '(.ddd)';
    const play   = elem.getAttribute( 'data-odometer-play-onload' ) || false;

    if ( userPrefersReducedMotion ) {
      elem.innerHTML = to;
      return;
    }

    // set element to initial number
    elem.innerHTML = from;

    const od = new Odometer({
      el: elem,
      value: from,
      format: format,
      duration: dur
    });

    if ( dur !== 3000 ) {
      elem.style.setProperty( '--transition-duration', dur + 'ms' );
    }
    if ( play ) {
      window.addEventListener( 'load', () => od.update( to ) );
    }

    return od;
  };

  /**
   * Initialize Toggler library.
   */
  new Toggler( 'data-toggle' );

  /**
   * Initialize Scrollstep library.
   */
  new Scrollstep( '[data-scrollstep]' );

  /**
   * Initialize Carousel elements.
   */
  [].forEach.call( document.querySelectorAll( '[data-carousel]' ), ( elem ) => {
    elem.carousel = new Carousel( elem );
  });

  /**
   * Initialize Carousel elements.
   */
  [].forEach.call( document.querySelectorAll( '[data-odometer]' ), ( elem ) => {
    elem.odometer = odometerInit( elem );
  });

  /**
   * Initialize Parallax elements.
   */
  [].forEach.call( document.querySelectorAll( '[data-parallax]' ), function( scene ) {
    if ( userPrefersReducedMotion ) return;
    scene.parallax = new Parallax( scene, {
      selector: '[data-depth]',
      hoverOnly: true,
      pointerEvents: true
    });
  });

  /**
   * Initialize Tabs elements.
   */
  [].forEach.call( document.querySelectorAll( '[data-tabs]' ), ( elem ) => {
    elem.tabs = new Tabs( elem, {
      onChange: ( currentTab, previousTab ) => {
        previousTab.classList.remove( 'was-in-viewport' );
        currentTab.classList.add( 'was-in-viewport' );
      }
    });
    if ( elem.getAttribute( 'data-tabs-autoplay' ) ) {
      setInterval( () => {
        elem.tabs.next();
      }, parseInt( elem.getAttribute( 'data-tabs-autoplay' )) );
    }
  });

  /**
   * Disable submit button when form is being submitted.
   */
  [].forEach.call( document.querySelectorAll( 'form' ), ( form ) => {
    form.addEventListener( 'submit', disableFormSubmitButton.bind( null, form ) );
  });

  /**
   * Add ready class to <body> when app has loaded.
   */
  window.addEventListener( 'load', () => {
    bodyEl.classList.remove( 'dom-is-loading' );
    bodyEl.classList.add( 'dom-is-ready' );
  });

  /**
   * Add a custom class to <html> when user is using tabs to navigate the page.
   */
  window.addEventListener( 'keydown', addUserIsTabbingClass );

  /**
   * Initialize vertical navigation for products (home page).
   */
  [].forEach.call( document.querySelectorAll( '[data-carousel-products-nav]' ), ( elem ) => {
    const list  = elem.querySelector( 'ul' );
    const items = elem.querySelectorAll( 'li' );

    let currentItem = elem.querySelector( '[aria-selected]' ) || items[0];
    let currentIndex = [].indexOf.call( items, currentItem ) + 1;
    list.setAttribute( 'data-selected-item', currentIndex );

    elem.addEventListener( 'click', ( event ) => {
      let target = event.target;
      while ( target.nodeName !== 'LI' || target === document.documentElement ) {
        target = target.parentElement;
      }

      [].forEach.call( items, ( item ) => item.removeAttribute( 'aria-selected' ) );
      target.setAttribute( 'aria-selected', 'true' );

      currentItem = elem.querySelector( '[aria-selected]' );
      currentIndex = [].indexOf.call( items, currentItem ) + 1;
      list.setAttribute( 'data-selected-item', currentIndex );
    });
  });

}

export default App;

App();
